import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Share2, Bookmark, Facebook, Twitter, Linkedin, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CursorGlow from '../components/CursorGlow';
import ScrollProgress from '../components/ScrollProgress';

// Simple markdown renderer component
const MarkdownRenderer = ({ content }) => {
  const elements = useMemo(() => {
    const lines = content.split('\n');
    const result = [];
    let inCodeBlock = false;
    let codeContent = [];
    let inList = false;
    let listItems = [];
    let listType = 'ul';

    // Bold text formatter
    const formatText = (text) => {
      const parts = text.split(/(\*\*[^*]+\*\*)/g);
      return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
        }
        return part;
      });
    };

    const flushList = () => {
      if (listItems.length > 0) {
        if (listType === 'ul') {
          result.push(
            <ul key={`list-${result.length}`} className="list-disc list-inside space-y-2 my-4 text-gray-300">
              {listItems.map((item, i) => (
                <li key={i} className="ml-4">{item}</li>
              ))}
            </ul>
          );
        } else {
          result.push(
            <ol key={`list-${result.length}`} className="list-decimal list-inside space-y-2 my-4 text-gray-300">
              {listItems.map((item, i) => (
                <li key={i} className="ml-4">{item}</li>
              ))}
            </ol>
          );
        }
        listItems = [];
        inList = false;
      }
    };

    lines.forEach((line, index) => {
      // Code block handling
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          flushList();
          result.push(
            <pre key={`code-${index}`} className="bg-slate-800/80 border border-slate-700/50 rounded-xl p-4 my-6 overflow-x-auto">
              <code className="text-cyan-300 text-sm font-mono">
                {codeContent.join('\n')}
              </code>
            </pre>
          );
          codeContent = [];
          inCodeBlock = false;
        } else {
          flushList();
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        return;
      }

      // Headers
      if (line.startsWith('## ')) {
        flushList();
        result.push(
          <h2 key={`h2-${index}`} className="text-3xl font-bold text-cyan-400 mt-12 mb-6">
            {line.replace('## ', '')}
          </h2>
        );
        return;
      }

      if (line.startsWith('### ')) {
        flushList();
        result.push(
          <h3 key={`h3-${index}`} className="text-xl font-bold text-white mt-8 mb-4">
            {line.replace('### ', '')}
          </h3>
        );
        return;
      }

      // Unordered list
      if (line.startsWith('- ') || line.startsWith('* ')) {
        if (!inList || listType !== 'ul') {
          flushList();
          inList = true;
          listType = 'ul';
        }
        listItems.push(formatText(line.replace(/^[-*] /, '')));
        return;
      }

      // Ordered list
      if (line.match(/^\d+\. /)) {
        if (!inList || listType !== 'ol') {
          flushList();
          inList = true;
          listType = 'ol';
        }
        listItems.push(formatText(line.replace(/^\d+\. /, '')));
        return;
      }

      // Regular paragraph
      if (line.trim()) {
        flushList();
        result.push(
          <p key={`p-${index}`} className="text-gray-300 leading-relaxed mb-4">
            {formatText(line)}
          </p>
        );
      }
    });

    flushList();
    return result;
  }, [content]);

  return <div className="space-y-2">{elements}</div>;
};

const BlogDetail = () => {
  const { slug } = useParams();

  // Sample blog post data - in real app this would come from API
  const post = {
    slug: 'yapay-zeka-ile-mobil-uygulama-gelistirme',
    title: '2025\'te Yapay Zeka ile Mobil Uygulama Geliştirme: Kapsamlı Rehber',
    excerpt: 'Yapay zeka teknolojilerinin mobil uygulama geliştirme süreçlerini nasıl dönüştürdüğünü ve bu teknolojileri projelerinizde nasıl kullanabileceğinizi keşfedin.',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=600&fit=crop',
    category: 'Yapay Zeka',
    author: {
      name: 'Elif Demir',
      role: 'CTO',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face'
    },
    date: '10 Aralık 2025',
    readTime: '12 dk',
    content: `## Giriş

Yapay zeka, mobil uygulama geliştirme dünyasını köklü bir şekilde değiştiriyor. 2025 yılında, AI destekli araçlar ve kütüphaneler sayesinde geliştiriciler daha akıllı, daha kişiselleştirilmiş ve daha verimli uygulamalar oluşturabiliyorlar.

Bu kapsamlı rehberde, yapay zeka teknolojilerini mobil uygulama projelerinize nasıl entegre edebileceğinizi adım adım inceleyeceğiz.

## Yapay Zekanın Mobil Uygulamalarda Kullanım Alanları

### 1. Doğal Dil İşleme (NLP)

Chatbotlar, sesli asistanlar ve metin analizi gibi özellikler için NLP teknolojileri kullanılmaktadır. OpenAI'nin GPT modelleri ve Google'ın PaLM API'si bu alanda en popüler seçenekler arasında yer alıyor.

\`\`\`javascript
// ChatGPT API örnek kullanımı
const response = await openai.chat.completions.create({
  model: "gpt-4",
  messages: [
    { role: "system", content: "Sen yardımcı bir asistansın." },
    { role: "user", content: userMessage }
  ]
});
\`\`\`

### 2. Görüntü İşleme ve Tanıma

Yüz tanıma, nesne tespiti ve OCR (Optik Karakter Tanıma) gibi özellikler için görüntü işleme AI modelleri kullanılır.

- **TensorFlow Lite**: Mobil cihazlar için optimize edilmiş ML framework'ü
- **ML Kit**: Google'ın mobil ML çözümü
- **Core ML**: Apple'ın iOS için ML framework'ü

### 3. Kişiselleştirme ve Öneri Sistemleri

Kullanıcı davranışlarını analiz ederek kişiselleştirilmiş içerik ve ürün önerileri sunmak, kullanıcı deneyimini önemli ölçüde artırır.

## Mobil AI Entegrasyonu İçin En İyi Pratikler

### Performans Optimizasyonu

AI modellerini mobil cihazlarda çalıştırırken performans kritik önem taşır:

1. **Model Sıkıştırma**: Quantization teknikleri ile model boyutunu küçültün
2. **Edge Computing**: Mümkün olduğunca işlemleri cihaz üzerinde yapın
3. **Lazy Loading**: AI özelliklerini gerektiğinde yükleyin

### Gizlilik ve Güvenlik

Kullanıcı verilerini işlerken dikkat edilmesi gereken noktalar:

- On-device processing tercih edin
- Veri minimizasyonu uygulayın
- KVKK ve GDPR uyumluluğunu sağlayın

## Sonuç

Yapay zeka, mobil uygulama geliştirmenin geleceğini şekillendiriyor. Doğru araçlar ve stratejilerle, kullanıcılarınıza benzersiz ve akıllı deneyimler sunabilirsiniz.

RUYAN olarak, yapay zeka entegrasyonu konusunda uzmanız. Projeniz için AI çözümleri geliştirmek istiyorsanız, bizimle iletişime geçin.`,
    tags: ['Yapay Zeka', 'Mobil Uygulama', 'Machine Learning', 'ChatGPT', 'React Native']
  };

  const relatedPosts = [
    {
      id: 2,
      slug: 'react-native-vs-flutter-2025',
      title: 'React Native vs Flutter: 2025 Karşılaştırması',
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=300&fit=crop',
      date: '8 Aralık 2025'
    },
    {
      id: 5,
      slug: 'chatgpt-api-entegrasyonu',
      title: 'ChatGPT API\'yi Uygulamanıza Nasıl Entegre Edersiniz?',
      image: 'https://images.unsplash.com/photo-1676573409967-986dcf64d35a?w=400&h=300&fit=crop',
      date: '1 Aralık 2025'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Breadcrumb */}
      <section className="pt-24 pb-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-cyan-400 transition-colors">Ana Sayfa</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-cyan-400 transition-colors">Blog</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-400 truncate max-w-[200px]">{post.title}</span>
          </nav>
        </div>
      </section>

      {/* Hero */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Blog'a Dön
          </Link>

          <div className="mb-6">
            <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-sm font-medium rounded-full">
              {post.category}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            {post.title}
          </h1>

          <p className="text-xl text-gray-400 mb-8">{post.excerpt}</p>

          {/* Author & Meta */}
          <div className="flex flex-wrap items-center gap-6 pb-8 border-b border-slate-700/50">
            <div className="flex items-center gap-3">
              <img
                src={post.author.image}
                alt={post.author.name}
                className="w-12 h-12 rounded-full object-cover"
              />
              <div>
                <div className="text-white font-medium">{post.author.name}</div>
                <div className="text-gray-500 text-sm">{post.author.role}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Calendar className="w-4 h-4" />
              {post.date}
            </div>
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Clock className="w-4 h-4" />
              {post.readTime} okuma
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-auto rounded-2xl shadow-2xl"
          />
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-3">
              <article className="prose prose-invert prose-lg max-w-none">
                <MarkdownRenderer content={post.content} />
              </article>

              {/* Tags */}
              <div className="mt-12 pt-8 border-t border-slate-700/50">
                <h4 className="text-white font-semibold mb-4">Etiketler</h4>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-slate-800/50 text-gray-400 text-sm rounded-full hover:bg-slate-700/50 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share */}
              <div className="mt-8 pt-8 border-t border-slate-700/50">
                <h4 className="text-white font-semibold mb-4">Bu Yazıyı Paylaş</h4>
                <div className="flex gap-3">
                  <button className="w-10 h-10 bg-slate-800/50 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-all">
                    <Facebook className="w-5 h-5" />
                  </button>
                  <button className="w-10 h-10 bg-slate-800/50 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-sky-500 transition-all">
                    <Twitter className="w-5 h-5" />
                  </button>
                  <button className="w-10 h-10 bg-slate-800/50 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-700 transition-all">
                    <Linkedin className="w-5 h-5" />
                  </button>
                  <button className="w-10 h-10 bg-slate-800/50 rounded-full flex items-center justify-center text-gray-400 hover:text-cyan-400 transition-all">
                    <Share2 className="w-5 h-5" />
                  </button>
                  <button className="w-10 h-10 bg-slate-800/50 rounded-full flex items-center justify-center text-gray-400 hover:text-yellow-400 transition-all">
                    <Bookmark className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-8">
                {/* Author Card */}
                <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6">
                  <h4 className="text-white font-semibold mb-4">Yazar Hakkında</h4>
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={post.author.image}
                      alt={post.author.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-white font-medium">{post.author.name}</div>
                      <div className="text-cyan-400 text-sm">{post.author.role}</div>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">
                    10+ yıllık yazılım geliştirme deneyimi ile yapay zeka ve mobil teknolojiler konusunda uzman.
                  </p>
                </div>

                {/* CTA */}
                <div className="bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/30 rounded-2xl p-6">
                  <h4 className="text-white font-semibold mb-2">Projeniz mi var?</h4>
                  <p className="text-gray-400 text-sm mb-4">
                    AI entegrasyonu için bizimle iletişime geçin.
                  </p>
                  <Link
                    to="/iletisim"
                    className="block w-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-center py-3 rounded-xl font-medium hover:from-cyan-600 hover:to-teal-600 transition-all"
                  >
                    İletişime Geç
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8">İlgili Yazılar</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {relatedPosts.map((related) => (
              <Link
                key={related.id}
                to={`/blog/${related.slug}`}
                className="group flex gap-4 bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 hover:border-cyan-500/50 transition-all"
              >
                <img
                  src={related.image}
                  alt={related.title}
                  className="w-32 h-24 object-cover rounded-lg"
                />
                <div className="flex flex-col justify-center">
                  <h3 className="text-white font-medium group-hover:text-cyan-400 transition-colors line-clamp-2">
                    {related.title}
                  </h3>
                  <div className="text-gray-500 text-sm mt-2">{related.date}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogDetail;
